// Row for supply / bond auction events.

import { useState } from "react";
import type { SupplyResponse } from "../../types";
import { Flag } from "../Flag";
import { ImportanceIcon } from "../ImportanceIcon";
import { Markdown } from "../Markdown";
import { TimeCell } from "../TimeCell";
import {
  computeRowClasses,
  getCategory,
  getImportance,
  hasDetail,
  useChangeAnimation,
  type CommonRowProps,
} from "../rowCommon";

interface Metric {
  label: string;
  title: string;
  value: string | number;
}

function MetricsList({ items }: { items: Metric[] }) {
  if (items.length === 0) return <>-</>;
  return (
    <dl className="metrics">
      {items.map((item) => (
        <div className="metric" key={item.label}>
          <dt title={item.title}>{item.label}:</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SupplyRow(props: CommonRowProps) {
  useChangeAnimation(props);
  const [expanded, setExpanded] = useState(false);

  const event = props.event as SupplyResponse;
  const category = getCategory(props.event);
  const detail = event.detail;
  const location = category?.location || event.location || "";

  const actualItems: Metric[] = [];
  if (event.sold_quantity !== null && event.sold_quantity !== undefined) {
    actualItems.push({ label: "Sold", title: "Sold Quantity", value: event.sold_quantity });
  }
  if (event.bc_actual !== null && event.bc_actual !== undefined) {
    actualItems.push({ label: "BC", title: "Bid to Cover", value: event.bc_actual });
  }
  if (event.average_actual !== null && event.average_actual !== undefined) {
    actualItems.push({ label: "Avg", title: "Average", value: event.average_actual });
  }
  if (event.tail_actual !== null && event.tail_actual !== undefined) {
    actualItems.push({ label: "Tail", title: "Tail", value: event.tail_actual });
  }
  if (event.wi_actual !== null && event.wi_actual !== undefined) {
    actualItems.push({ label: "WI", title: "When Issued", value: event.wi_actual });
  }
  if (event.dealers_actual) {
    actualItems.push({ label: "Dealers", title: "Dealers", value: event.dealers_actual });
  }
  if (event.retention_actual) {
    actualItems.push({ label: "Retention", title: "Retention", value: event.retention_actual });
  }

  const previousItems: Metric[] = [];
  if (event.bc_previous !== null && event.bc_previous !== undefined) {
    previousItems.push({ label: "BC", title: "Bid to Cover", value: event.bc_previous });
  }
  if (event.average_previous !== null && event.average_previous !== undefined) {
    previousItems.push({ label: "Avg", title: "Average", value: event.average_previous });
  }
  if (event.tail_previous !== null && event.tail_previous !== undefined) {
    previousItems.push({ label: "Tail", title: "Tail", value: event.tail_previous });
  }
  if (event.wi_previous !== null && event.wi_previous !== undefined) {
    previousItems.push({ label: "WI", title: "When Issued", value: event.wi_previous });
  }
  if (event.dealers_previous) {
    previousItems.push({ label: "Dealers", title: "Dealers", value: event.dealers_previous });
  }
  if (event.retention_previous) {
    previousItems.push({ label: "Retention", title: "Retention", value: event.retention_previous });
  }

  const detailSpan =
    (props.showLowHigh ? 8 : 6) + (props.chart === "mini" ? 1 : 0);

  return (
    <>
      <div className={computeRowClasses(props) + " calendar-grid-row"}>
        <div className="cell-location">
          <span className="flag-container">
            <Flag region={location} />
          </span>
        </div>
        <div className="cell-time">
          <TimeCell event={props.event} />
        </div>
        <div className="cell-title">
          {hasDetail(props.event) && (
            <button onClick={() => setExpanded((v) => !v)} className="expand-toggle">
              {expanded ? "▼" : "▶"}
            </button>
          )}
          <div className="info">
            <span className="event-name">{event.summary}</span>
            {event.isin && <span className="supply-isin"> ({event.isin})</span>}
          </div>
        </div>
        <div className="cell-actual multi-line">
          <MetricsList items={actualItems} />
        </div>
        {props.chart === "mini" && <div className="cell-chart">&nbsp;</div>}
        <div className="cell-expected">-</div>
        {props.showLowHigh && (
          <>
            <div className="cell-low">-</div>
            <div className="cell-high">-</div>
          </>
        )}
        <div className="cell-previous multi-line">
          <MetricsList items={previousItems} />
        </div>
        <div className="cell-revised">-</div>
        <div className="cell-importance">
          <ImportanceIcon importance={getImportance(props.event)} size="md" />
        </div>
      </div>
      {expanded && detail && (
        <div className="detail-row calendar-grid-row">
          <div className="cell-location">&nbsp;</div>
          <div className="cell-time">&nbsp;</div>
          <div className="cell-title" style={{ gridColumn: `span ${detailSpan}` }}>
            <div className="detail-content">
              <Markdown content={detail} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SupplyRow;
